import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, MessageCircle, PencilLine, ShieldCheck, Users } from "lucide-react";
import { bookingService } from "@/lib/services";
import { PageShell } from "@/components/nav";
import { extractErrorMessage } from "@/lib/api";
import { formatDate, formatUsh } from "@/lib/format";
import { CancelBookingDialog } from "@/components/cancel-booking-dialog";
import { ChangeDatesDialog } from "@/components/change-dates-dialog";
import { BookingPaymentsPanel } from "@/components/booking-payments-panel";
import { useAuth } from "@/lib/auth-context";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/bookings/$bookingId")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData({
      queryKey: ["booking", params.bookingId],
      queryFn: () => bookingService.get(params.bookingId),
    }),
  component: BookingDetail,
});

function BookingDetail() {
  const { bookingId } = Route.useParams();
  const router = useRouter();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => bookingService.get(bookingId),
  });
  const booking = data?.data;

  const { user } = useAuth();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);

  const refreshBooking = () => {
    qc.invalidateQueries({ queryKey: ["guest-bookings"] });
    qc.invalidateQueries({ queryKey: ["booking", bookingId] });
    qc.invalidateQueries({ queryKey: ["listing-reservations"] });
    router.invalidate();
  };

  if (!booking) return null;
  const cancellable = booking.status === "pending" || booking.status === "confirmed";

  return (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/bookings" className="text-xs font-semibold text-ink-muted hover:text-ink">
          ← Back to bookings
        </Link>

        <div className="mt-4 rounded-3xl border border-border bg-card p-6 shadow-[var(--shadow-card)] md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
              {booking.status.replace("_", " ")}
            </span>
          </div>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink">
            {booking.property?.name ?? "Your stay"}
          </h1>

          <dl className="mt-6 grid grid-cols-2 gap-4">
            <Stat icon={CalendarDays} label="Check in" value={formatDate(booking.check_in_date)} />
            <Stat
              icon={CalendarDays}
              label="Check out"
              value={formatDate(booking.check_out_date)}
            />
            <Stat icon={Users} label="Guests" value={String(booking.guest_count)} />
            <Stat icon={CalendarDays} label="Total" value={formatUsh(booking.total)} />
          </dl>

          {booking.notes && (
            <div className="mt-6 rounded-2xl border border-border bg-muted/30 p-4 text-sm text-ink">
              <p className="text-xs font-bold uppercase tracking-wider text-ink-subtle">Notes</p>
              <p className="mt-1">{booking.notes}</p>
            </div>
          )}

          {booking.details?.length > 0 && (
            <section className="mt-8">
              <h2 className="text-lg font-extrabold tracking-tight text-ink">Line items</h2>
              <ul className="mt-3 divide-y divide-border rounded-2xl border border-border bg-background">
                {booking.details.map((d) => (
                  <li key={d.id} className="flex items-center justify-between p-4 text-sm">
                    <div>
                      <p className="font-semibold text-ink">Listing {d.listing_id.slice(-6)}</p>
                      <p className="text-xs text-ink-muted">
                        {d.nights} night{d.nights === 1 ? "" : "s"} × {formatUsh(d.price_per_night)}
                      </p>
                    </div>
                    <span className="font-bold text-ink">
                      {formatUsh(Number(d.price_per_night) * d.nights)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/chats/$bookingId"
              params={{ bookingId: booking.id }}
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-secondary px-4 text-sm font-bold text-secondary-foreground hover:opacity-90"
            >
              {/* Whoever is on the other side of this booking. A host opening
                  their own property's booking is messaging the guest. */}
              <MessageCircle className="h-4 w-4" />{" "}
              {booking.guest_id === user?.id ? "Message host" : "Message guest"}
            </Link>
            {cancellable && (
              <>
                <button
                  onClick={() => setDatesOpen(true)}
                  className="inline-flex h-11 items-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-bold text-ink hover:bg-muted"
                >
                  <PencilLine className="h-4 w-4" /> Change dates
                </button>
                <button
                  onClick={() => setCancelOpen(true)}
                  className="inline-flex h-11 items-center rounded-xl border border-destructive/40 px-4 text-sm font-bold text-destructive hover:bg-destructive/5"
                >
                  Cancel booking
                </button>
              </>
            )}
          </div>

          {booking.status === "cancelled" && booking.refund_amount !== null && (
            <div className="mt-6 rounded-2xl border border-border bg-muted/30 p-4 text-sm">
              <p className="flex items-center gap-2 font-bold text-ink">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Cancelled{booking.cancelled_by_role === "host" ? " by the host" : ""}
              </p>
              <p className="mt-1 text-ink-muted">
                {formatUsh(booking.refund_amount ?? 0)} refundable
                {booking.cancellation_policy_applied
                  ? ` under the ${booking.cancellation_policy_applied.replace("_", "-")} policy`
                  : ""}
                .
                {booking.refund_status === "pending" && " Your host will be in touch to settle it."}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 pb-10">
        {/*
          The host of the property records movements; the guest reads them. Until
          a provider is wired in, only the host knows money actually arrived.
        */}
        <BookingPaymentsPanel
          bookingId={booking.id}
          canRecord={Boolean(user && booking.property?.host_id === user.id)}
        />
      </div>

      <CancelBookingDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        bookingId={booking.id}
        onCancelled={refreshBooking}
      />

      <ChangeDatesDialog
        open={datesOpen}
        onClose={() => setDatesOpen(false)}
        booking={booking}
        onChanged={refreshBooking}
      />
    </PageShell>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof CalendarDays;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-background p-4">
      <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-ink-subtle">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <p className="mt-1 text-base font-extrabold text-ink">{value}</p>
    </div>
  );
}
